// Marcador presencial (lote 6, item 5), sobre @truco6/marcador.
// - /marcador: Prata e Ouro criam o placar ao vivo (lados com nome ou trio fixo) e veem o histórico das noites. Grátis,
//   Bronze e convidado marcam só no aparelho, como no site, com cadeado no placar ao vivo e no histórico.
// - /placar/CODIGO: o placar ao vivo, aberto para qualquer um, sem conta. Quem tem a caneta marca; ?caneta=TOKEN pega a
//   caneta; ?telao=1 é o modo telão (paisagem, tela cheia e números enormes, para a TV ou o tablet do bar).
// - /marcador/noites: o histórico das noites (data, lados, partidas e placar).
// O marcador não dá Rodagem, pontos, missão, ofensiva nem badge.
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { renderSVG } from 'uqr';
import {
  type Lado, NOME_MAX, NOMES_PADRAO, type Placar, SOMAS, avisoDa, deTexto, desfazer, faltam, novaPartida, novoPlacar, paraTexto, renomear, somar,
} from '@truco6/marcador';
import { pode } from '@truco6/planos';
import type { PainelTrios } from '@truco6/gamificacao';
import { ErroDaApi, lerTrios } from '../comum/api.ts';
import { avisarNaTela } from '../comum/aviso.tsx';
import { guardarDestino } from '../comum/chegada.ts';
import { conta, type PerfilConta, type Sessao } from '../comum/conta.ts';
import {
  type Acao, type EscolhaDoLado, type LadoDoMarcador, type NoiteNoHistorico, type PlacarDeQuemMarca, criarPlacar, encerrarNoite, lerHistorico, linkDoPlacar,
  manterTelaAcesa, marcar, ouvirPlacar, passarCaneta, pegarCaneta, retomarCaneta, verPlacar,
} from '../comum/marcador.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { Escudo, VerPlanos } from '../liga/trios.tsx';
import { Marca } from '@truco6/ui/Marca';
import { Carregando, Tela, Topo } from './comum.tsx';

const ROTULOS = ['NÓS', 'ELES'] as const, CLASSES = ['nos', 'eles'] as const;
const DATA = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric' });
const HORA = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' });

function textoDoAviso(p: Placar): { texto: string; classe: string } | null {
  const a = avisoDa(p);
  if (!a) return null;
  if (a.tipo === 'mao_de_onze') return { texto: `Mão de onze para ${p.nomes[a.lado]}`, classe: 'onze' };
  if (a.tipo === 'mao_de_ferro') return { texto: 'Mão de ferro', classe: 'ferro' };
  return { texto: `${p.nomes[a.lado]} venceu`, classe: 'vitoria' };
}

function TelaAcesa() {
  const [acesa, setAcesa] = useState(false);
  useEffect(() => manterTelaAcesa(setAcesa), []);
  return acesa ? <span className="mk-acesa"><i aria-hidden="true" />Tela acesa</span> : null;
}

// O placar: dois lados com +1, +3, +6, +9 e +12 (quando pode marcar), o aviso da próxima mão, as partidas da noite,
// Desfazer e Nova partida. No telão, só os números e os nomes, enormes.
export function PainelDoPlacar({ p, lados, podeMarcar, aoAcao, ocupado = false, telao = false, aoRenomear }: {
  p: Placar; lados?: [LadoDoMarcador, LadoDoMarcador]; podeMarcar: boolean; aoAcao: (a: Acao) => void; ocupado?: boolean; telao?: boolean;
  aoRenomear?: (lado: Lado, nome: string) => void;
}) {
  const aviso = textoDoAviso(p), ultima = p.historico[p.historico.length - 1];
  const lado = (l: Lado) => (
    <section key={l} className={`mk-lado ${CLASSES[l]}`} aria-label={`Placar de ${p.nomes[l]}`} style={l === 1 ? { order: 3 } : undefined}>
      <div className="mk-cab">
        <span className="mk-rot">{ROTULOS[l]}</span>
        {lados?.[l]?.trio && <Escudo id={lados[l].trio!.escudo} tamanho={telao ? 40 : 26} />}
        {aoRenomear ? (
          <input type="text" maxLength={NOME_MAX} autoComplete="off" spellCheck={false} aria-label={`Nome de ${ROTULOS[l]}`} defaultValue={p.nomes[l]} key={p.nomes[l]}
            onBlur={(ev) => aoRenomear(l, ev.target.value)} onKeyDown={(ev) => { if (ev.key === 'Enter') (ev.target as HTMLInputElement).blur(); }} />
        ) : <b className="mk-nome">{p.nomes[l]}</b>}
      </div>
      <div className="mk-placar">
        <span className="mk-num" role="status" aria-live="polite" aria-label={`${p.nomes[l]}: ${p.pontos[l]}`}>{p.pontos[l]}</span>
        <span className="mk-faltam">{faltam(p, l) > 0 ? `Faltam ${faltam(p, l)}` : 'Venceu'}</span>
      </div>
      {podeMarcar && !telao && (
        <div className="mk-somas" role="group" aria-label={`Somar para ${p.nomes[l]}`}>
          {SOMAS.map((s) => (
            <button key={s} type="button" disabled={ocupado || p.vencedor !== null} aria-label={`Mais ${s} para ${p.nomes[l]}`}
              onClick={() => aoAcao({ acao: 'somar', lado: l, soma: s })}>+{s}</button>
          ))}
        </div>
      )}
    </section>
  );
  return (
    <div className={'mk-corpo' + (telao ? ' telao' : '')}>
      {lado(0)}
      <div className="mk-meio" style={{ order: 2 }}>
        <div className={'mk-aviso' + (aviso ? ' ' + aviso.classe : '')} role="alert" aria-live="assertive">{aviso?.texto ?? ''}</div>
        <div className="mk-linha"><span className="mk-noite" aria-label="Partidas da noite">Partidas: <b>{p.vitorias[0]}</b> x <b>{p.vitorias[1]}</b></span></div>
        {podeMarcar && !telao && (
          <div className="mk-linha mk-acoes">
            <button type="button" disabled={ocupado || !p.historico.length} onClick={() => aoAcao({ acao: 'desfazer' })}>Desfazer</button>
            <button type="button" className="nova" disabled={ocupado} onClick={() => aoAcao({ acao: 'nova_partida' })}>Nova partida</button>
          </div>
        )}
        {!telao && <p className="mk-hist" aria-live="polite">{ultima ? `Última: ${p.nomes[ultima.lado]} +${ultima.soma} · ${p.pontos[0]} x ${p.pontos[1]}` : ''}</p>}
      </div>
      {lado(1)}
    </div>
  );
}

// O marcador só no aparelho: o do site, dentro do jogo. Guarda o placar neste navegador.
const CHAVE_LOCAL = 't6_marcador_jogo';
function MarcadorLocal() {
  const [p, setP] = useState<Placar>(() => { try { return deTexto(localStorage.getItem(CHAVE_LOCAL)) ?? novoPlacar(); } catch { return novoPlacar(); } });
  const mudar = (n: Placar) => { setP(n); try { localStorage.setItem(CHAVE_LOCAL, paraTexto(n)); } catch { /* sem armazenamento */ } };
  const acao = (a: Acao) => {
    if (a.acao === 'somar') mudar(somar(p, a.lado, a.soma as (typeof SOMAS)[number]));
    else if (a.acao === 'desfazer') mudar(desfazer(p));
    else if (a.acao === 'nova_partida') mudar(novaPartida(p));
  };
  return <PainelDoPlacar p={p} podeMarcar aoAcao={acao} aoRenomear={(l, nome) => mudar(renomear(p, l, nome))} />;
}

// Escolha de um lado: nome livre ou um dos seus trios fixos.
function EscolhaLado({ lado, trios, valor, aoMudar }: { lado: Lado; trios: PainelTrios['trios']; valor: EscolhaDoLado; aoMudar: (v: EscolhaDoLado) => void }) {
  const trio = 'trio' in valor ? valor.trio : '';
  return (
    <div className={`mk-escolha ${CLASSES[lado]}`}>
      <span className="mk-rot">{ROTULOS[lado]}</span>
      {trios.length > 0 && (
        <select aria-label={`Trio fixo de ${ROTULOS[lado]}`} value={trio} onChange={(ev) => aoMudar(ev.target.value ? { trio: ev.target.value } : { nome: NOMES_PADRAO[lado] })}>
          <option value="">Nome livre</option>
          {trios.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
      )}
      {!trio && (
        <input type="text" maxLength={NOME_MAX} aria-label={`Nome de ${ROTULOS[lado]}`} value={'nome' in valor ? valor.nome : ''} placeholder={NOMES_PADRAO[lado]}
          onChange={(ev) => aoMudar({ nome: ev.target.value })} />
      )}
    </div>
  );
}

export function MarcadorTela({ sessao, perfil }: { sessao: Sessao; perfil: PerfilConta }) {
  const liberado = pode(perfil.plano, 'marcadorPresencial');
  const [trios, setTrios] = useState<PainelTrios['trios']>([]);
  const [lados, setLados] = useState<[EscolhaDoLado, EscolhaDoLado]>([{ nome: NOMES_PADRAO[0] }, { nome: NOMES_PADRAO[1] }]);
  const [aberta, setAberta] = useState<NoiteNoHistorico | null>(null);
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    if (!liberado) return;
    let vivo = true;
    lerTrios(sessao).then((t) => { if (vivo) setTrios(t.trios); }, () => {});
    lerHistorico(sessao).then((h) => { if (vivo) setAberta(h.noites.find((n) => n.aberta) ?? null); }, () => {});
    return () => { vivo = false; };
  }, [sessao.id, liberado]);
  async function criar() {
    setCriando(true); setErro(null);
    try {
      const p = await criarPlacar(sessao, lados);
      ir({ tela: 'placar', codigo: p.codigo, caneta: null, telao: false });
    } catch (e) { setErro(e instanceof Error ? e.message : 'Não deu certo. Tente de novo.'); } finally { setCriando(false); }
  }
  return (
    <Tela larga>
      <div className="topo-com-acao">
        <Topo titulo="Marcador" voltar={voltar} marca />
        <TelaAcesa />
      </div>
      {liberado ? (
        <section className="cartao mk-criar" aria-label="Placar ao vivo">
          <h2 className="rotulo">PLACAR AO VIVO PARA A MESA TODA</h2>
          <p className="nota">Quem abrir o link ou o QR code vê o placar na hora, sem conta. Só você marca, até passar a caneta.</p>
          {aberta && (
            <button className="btn2 dourado" onClick={() => ir({ tela: 'placar', codigo: aberta.codigo, caneta: null, telao: false })}>
              Voltar ao placar de hoje · {aberta.lados[0].nome} x {aberta.lados[1].nome}
            </button>
          )}
          <div className="mk-escolhas">
            {([0, 1] as const).map((l) => <EscolhaLado key={l} lado={l} trios={trios} valor={lados[l]} aoMudar={(v) => setLados((x) => (l === 0 ? [v, x[1]] : [x[0], v]))} />)}
          </div>
          {erro && <p className="erro" role="alert">{erro}</p>}
          <button className="btn1 largo" disabled={criando} onClick={() => void criar()}>{aberta ? 'COMEÇAR OUTRA NOITE' : 'CRIAR PLACAR AO VIVO'}</button>
          <button className="link" onClick={() => ir({ tela: 'noites' })}>Histórico das noites</button>
        </section>
      ) : (
        <section className="cartao mk-travado" aria-label="Placar ao vivo, com cadeado">
          <ul className="itens-travados" aria-label="O que entra">
            <li>Placar ao vivo no celular de todo mundo, por link e QR code</li>
            <li>Lados com o nome e o escudo do trio fixo</li>
            <li>Histórico das noites e modo telão</li>
          </ul>
          <div className="cadeado-tentos">
            <p className="cadeado">O placar ao vivo e o histórico das noites são dos planos Prata e Ouro. Aqui embaixo, o marcador só neste aparelho.</p>
            <VerPlanos />
          </div>
        </section>
      )}
      {!liberado && <MarcadorLocal />}
      {liberado && (
        <details className="cartao mk-local">
          <summary>Marcar só neste aparelho</summary>
          <MarcadorLocal />
        </details>
      )}
    </Tela>
  );
}

export function Qr({ texto, rotulo }: { texto: string; rotulo: string }) {
  const svg = useMemo(() => renderSVG(texto, { border: 2, pixelSize: 6, whiteColor: '#FBF8F1', blackColor: '#071811' }), [texto]);
  return <div className="mk-qr" role="img" aria-label={rotulo} dangerouslySetInnerHTML={{ __html: svg }} />;
}

function Folha({ titulo, aoFechar, children }: { titulo: string; aoFechar: () => void; children: React.ReactNode }) {
  return createPortal(
    <div className="folha-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) aoFechar(); }}>
      <div className="folha simples mk-folha" role="dialog" aria-modal="true" aria-label={titulo}>
        <h2>{titulo}</h2>
        {children}
        <button className="btn2" onClick={aoFechar}>Fechar</button>
      </div>
    </div>,
    document.body,
  );
}

async function copiar(texto: string, aviso: string) {
  try { await navigator.clipboard.writeText(texto); avisarNaTela(aviso); } catch { avisarNaTela('Não deu para copiar. Segure no link para copiar.'); }
}

// /placar/CODIGO: o placar ao vivo. Aberto para qualquer um; quem tem a caneta marca.
export function PlacarAoVivoTela({ sessao, codigo, caneta, telao }: { sessao: Sessao | null; codigo: string; caneta: string | null; telao: boolean }) {
  const [p, setP] = useState<PlacarDeQuemMarca | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [folha, setFolha] = useState<'compartilhar' | 'caneta' | 'encerrar' | null>(null);
  const [linkCaneta, setLinkCaneta] = useState<{ link: string; ate: number } | null>(null);
  const id = sessao?.id ?? null;
  useEffect(() => {
    let vivo = true;
    verPlacar(sessao, codigo).then((x) => { if (vivo) setP(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    const parar = ouvirPlacar(codigo, (novo) => {
      // O placar chega para todo mundo; quem tem conta confere de novo se está com a caneta.
      setP((a) => (a ? { ...a, ...novo } : { ...novo, souCaneta: false, souDono: false }));
      if (sessao) verPlacar(sessao, codigo).then((x) => { if (vivo) setP(x); }, () => {});
    });
    return () => { vivo = false; parar(); };
  }, [codigo, id]);
  useEffect(() => {
    if (!telao) return;
    document.documentElement.requestFullscreen?.().catch(() => {});
    return () => { if (document.fullscreenElement) void document.exitFullscreen().catch(() => {}); };
  }, [telao]);

  if (erro && !p) return <Tela><Topo titulo="Placar ao vivo" voltar={voltar} marca /><p className="erro" role="alert">{erro}</p></Tela>;
  if (!p) return <Tela><Carregando /></Tela>;
  const placar = p;

  async function acao(a: Acao) {
    if (!sessao) return;
    setOcupado(true);
    try { setP(await marcar(sessao, codigo, a, placar.versao)); } catch (e) {
      avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.');
      verPlacar(sessao, codigo).then(setP, () => {});
    } finally { setOcupado(false); }
  }
  async function gerarLinkDaCaneta() {
    if (!sessao) return;
    try { setLinkCaneta(await passarCaneta(sessao, codigo)); setFolha('caneta'); } catch (e) { avisarNaTela(e instanceof Error ? e.message : 'Não deu certo.'); }
  }
  async function pegar() {
    if (!sessao || !caneta) return;
    try { setP(await pegarCaneta(sessao, codigo, caneta)); avisarNaTela('A caneta é sua. Agora você marca.'); ir({ tela: 'placar', codigo, caneta: null, telao: false }, true); } catch (e) {
      avisarNaTela(e instanceof Error ? e.message : 'Não deu certo.');
    }
  }
  const link = linkDoPlacar(codigo);
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`Acompanhe o placar da nossa noite de truco: ${link}`)}`;

  if (telao) {
    return (
      <div className="mk-telao">
        <div className="mk-telao-topo">
          <Marca altura={40} />
          <span className="mk-ao-vivo"><i aria-hidden="true" />{placar.encerrada ? 'Noite encerrada' : 'Ao vivo'}</span>
          <TelaAcesa />
          <button className="mk-sair-telao" onClick={() => ir({ tela: 'placar', codigo, caneta: null, telao: false }, true)} aria-label="Sair do telão">✕</button>
        </div>
        <PainelDoPlacar p={placar.placar} lados={placar.lados} podeMarcar={false} aoAcao={() => {}} telao />
      </div>
    );
  }

  return (
    <Tela larga>
      <div className="topo-com-acao">
        <Topo titulo="Placar ao vivo" voltar={voltar} marca />
        <TelaAcesa />
      </div>
      <div className="mk-faixa">
        <span className="mk-ao-vivo"><i aria-hidden="true" />{placar.encerrada ? 'Noite encerrada' : 'Ao vivo'}</span>
        <span className="nota">Quem marca: <b>{placar.souCaneta ? 'Você' : placar.caneta}</b></span>
      </div>
      {caneta && !placar.souCaneta && !placar.encerrada && (
        <section className="cartao mk-pegar" aria-label="Pegar a caneta">
          <p><b>{placar.caneta} passou a caneta para você.</b> Com ela, você marca o placar da mesa.</p>
          {sessao && !sessao.anonimo
            ? <button className="btn2 dourado" onClick={() => void pegar()}>Pegar a caneta</button>
            : <button className="btn2 dourado" onClick={() => {
              guardarDestino({ tela: 'placar', codigo, caneta, telao: false });
              ir({ tela: 'inicio' });
            }}>Entrar com Google para pegar a caneta</button>}
        </section>
      )}
      <PainelDoPlacar p={placar.placar} lados={placar.lados} podeMarcar={placar.souCaneta && !placar.encerrada} aoAcao={(a) => void acao(a)} ocupado={ocupado} />
      <div className="mk-botoes">
        <button className="btn2" onClick={() => setFolha('compartilhar')}>Compartilhar</button>
        <button className="btn2" onClick={() => ir({ tela: 'placar', codigo, caneta: null, telao: true })}>Modo telão</button>
        {placar.souCaneta && !placar.encerrada && <button className="btn2" onClick={() => void gerarLinkDaCaneta()}>Passar a caneta</button>}
        {placar.souDono && !placar.souCaneta && !placar.encerrada && <button className="btn2" onClick={() => sessao && retomarCaneta(sessao, codigo).then(setP, (e: Error) => avisarNaTela(e.message))}>Retomar a caneta</button>}
        {placar.souDono && !placar.encerrada && <button className="btn2 perigo" onClick={() => setFolha('encerrar')}>Encerrar a noite</button>}
      </div>
      {placar.partidas.length > 0 && (
        <section className="cartao" aria-label="Partidas da noite">
          <h2 className="rotulo">PARTIDAS DA NOITE</h2>
          <ol className="mk-partidas">
            {placar.partidas.map((x, i) => (
              <li key={i}><span>{i + 1}ª · {HORA.format(new Date(x.em))}</span><b>{x.pontos[0]} x {x.pontos[1]}</b><small>{placar.placar.nomes[x.vencedor]} venceu</small></li>
            ))}
          </ol>
        </section>
      )}
      <p className="nota centro">O marcador não dá Rodagem, pontos nem missão: a partida no bar ninguém confere.</p>
      {folha === 'compartilhar' && (
        <Folha titulo="Placar para a mesa toda" aoFechar={() => setFolha(null)}>
          <Qr texto={link} rotulo="QR code do placar ao vivo" />
          <p className="mk-link">{link}</p>
          <div className="mk-dupla">
            <button className="btn2" onClick={() => void copiar(link, 'Link do placar copiado.')}>Copiar link</button>
            <a className="btn2 dourado" href={whatsapp} target="_blank" rel="noopener">WhatsApp</a>
          </div>
        </Folha>
      )}
      {folha === 'caneta' && linkCaneta && (
        <Folha titulo="Passar a caneta" aoFechar={() => setFolha(null)}>
          <p className="nota">Quem abrir este link com a conta Google passa a marcar. Vale até {HORA.format(new Date(linkCaneta.ate))} e uma vez só.</p>
          <Qr texto={linkCaneta.link} rotulo="QR code para pegar a caneta" />
          <p className="mk-link">{linkCaneta.link}</p>
          <button className="btn2" onClick={() => void copiar(linkCaneta.link, 'Link da caneta copiado.')}>Copiar link da caneta</button>
        </Folha>
      )}
      {folha === 'encerrar' && (
        <Folha titulo="Encerrar a noite?" aoFechar={() => setFolha(null)}>
          <p>O placar fica no histórico das noites e ninguém marca mais.</p>
          <button className="btn2 perigo" onClick={() => { setFolha(null); if (sessao) encerrarNoite(sessao, codigo).then(setP, (e: Error) => avisarNaTela(e.message)); }}>Encerrar a noite</button>
        </Folha>
      )}
    </Tela>
  );
}

// /marcador/noites: as noites com placar ao vivo, da mais nova para a mais antiga.
export function NoitesTela({ sessao, perfil }: { sessao: Sessao; perfil: PerfilConta }) {
  const liberado = pode(perfil.plano, 'marcadorPresencial');
  const [noites, setNoites] = useState<NoiteNoHistorico[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    if (!liberado) return;
    let vivo = true;
    lerHistorico(sessao).then((h) => { if (vivo) setNoites(h.noites); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id, liberado]);
  return (
    <Tela>
      <Topo titulo="Histórico das noites" voltar={voltar} marca />
      {!liberado ? (
        <div className="cartao cadeado-tentos"><p className="cadeado">O histórico das noites é dos planos Prata e Ouro.</p><VerPlanos /></div>
      ) : erro ? <p className="erro" role="alert">{erro}</p> : !noites ? <Carregando /> : !noites.length ? (
        <p className="cartao vazio-lista">Nenhuma noite ainda. Crie um placar ao vivo no Marcador.</p>
      ) : (
        <ul className="mk-noites">
          {noites.map((n) => (
            <li key={n.codigo} className="cartao">
              <button className="mk-noite-linha" onClick={() => ir({ tela: 'placar', codigo: n.codigo, caneta: null, telao: false })}>
                <span className="data">{DATA.format(new Date(n.criadaEm))}{n.aberta && <small className="etiqueta">Ao vivo</small>}</span>
                <span className="lados">
                  <span>{n.lados[0].trio && <Escudo id={n.lados[0].trio.escudo} tamanho={20} />}{n.lados[0].nome}</span>
                  <b>{n.vitorias[0]} x {n.vitorias[1]}</b>
                  <span>{n.lados[1].trio && <Escudo id={n.lados[1].trio.escudo} tamanho={20} />}{n.lados[1].nome}</span>
                </span>
                <small>{n.partidas.length} {n.partidas.length === 1 ? 'partida' : 'partidas'}{n.partidas.length ? ': ' + n.partidas.map((x) => `${x.pontos[0]} x ${x.pontos[1]}`).join(', ') : ''}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Tela>
  );
}
